/* eslint-disable no-unused-expressions */
import { useCallback, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Button from '@zenbusiness/zui/Button';
import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { useNavigate, generatePath } from 'react-router-dom';

import AccountRow from '#client/components/AccountRow';
import ConfirmModal from '#client/components/ConfirmModal';
import MenuItem from '#client/components/MenuItem';
import MenuItemConnectedAccount from '#client/components/MenuItemConnectedAccount';
import MoveMoneyChoiceViewModal from '#client/components/MoveMoney/MoveMoneyChoiceViewModal';
import PaymentStatusLabel from '#client/components/PaymentStatusLabel';
import { MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS_LABEL } from '#client/constants/payees';
import useBankingPath from '#client/hooks/useBankingPath';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalContext from '#client/hooks/useModalContext';
import useRedirectToLinkedAccounts from '#client/hooks/useRedirectToLinkedAccounts';
import { isActiveStatus, getRelinkActionLabel } from '#client/utils/counterpartyStatus';

import { LinkedBankAccountWrapper } from '../LinkBankAccountContainer.styles';

export default function LinkedBankAccount({
  id,
  bank,
  name,
  accountNumber,
  status,
  accountType,
  usedAt = null,
  connectedType,
  fullAccountNumber,
  wire,
  isLatest = false,
  disable = false,
  isACHSupported,
  isWireSupported
}) {
  const isArchived = !isActiveStatus(status);
  const relinkActionLabel = getRelinkActionLabel(status);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { openModal } = useModalContext();
  const theme = useTheme();
  const navigate = useNavigate();

  const usedAtDashboard = usedAt && usedAt === 'dashboard';
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { linkedAccountsPath, redirectToLinkedAccounts } =
    useRedirectToLinkedAccounts(businessEntityUuid);
  const bankingPath = useBankingPath();
  const handleOpen = (event) => {
    event?.stopPropagation();
    setIsOpenModal(true);
    setAnchorEl(event.currentTarget);
    event.currentTarget.blur();
  };

  const handleClose = useCallback((event) => {
    event?.stopPropagation();
    setIsOpenModal(false);
    setAnchorEl(null);
  }, []);

  const redirectToLinkedAccountsPage = useCallback(
    (event) => {
      navigate(linkedAccountsPath);
      handleClose(event);
    },
    [handleClose, navigate, linkedAccountsPath]
  );

  const openUnlinkConfirmModal = useCallback(
    (event) => {
      openModal({
        component: ConfirmModal,
        handleOK: redirectToLinkedAccountsPage,
        title: 'Continue to Linked Accounts to unlink this bank account?',
        message:
          'Unlinking is completed on the Linked Accounts page. You can relink this account there later if needed.',
        okText: 'Go to Linked Accounts',
        cancelText: 'Cancel'
      });

      handleClose(event);
    },
    [openModal, redirectToLinkedAccountsPage, handleClose]
  );

  const trackMoveMoneyClick = useButtonPressedCallback({
    elementName: 'move_money_lnkacc_button',
    elementText: 'Move money',
    appPageName: 'connected_accounts_screen'
  });

  const handleClickMoveMoneyButton = useCallback(
    (event) => {
      trackMoveMoneyClick();
      handleClose(event);
      openModal({
        component: MoveMoneyChoiceViewModal,
        name: MODAL_NAMES.MOVE_MONEY_CHOICE_VIEW_MODAL,
        // Hide check deposit when opened from dashboard Banks
        // or the transaction history connected-account details view.
        // should hide check deposit option
        isHideCheckDepositOption: true,
        entryPageForSendMoney: linkedAccountsPath,
        counterParty: {
          id,
          bank,
          name,
          accountNumber,
          isArchived,
          accountType,
          connectedType,
          fullAccountNumber,
          wire,
          isACHSupported,
          isWireSupported
        }
      });
    },
    [
      openModal,
      id,
      bank,
      linkedAccountsPath,
      handleClose,
      name,
      accountNumber,
      isArchived,
      accountType,
      connectedType,
      fullAccountNumber,
      wire,
      isACHSupported,
      isWireSupported,
      trackMoveMoneyClick
    ]
  );

  const relinkHandler = useCallback(
    (event) => {
      event?.stopPropagation();
      redirectToLinkedAccounts({ useTopWindow: true });
    },
    [redirectToLinkedAccounts]
  );

  /**
   * Using on the dashboard
   * When user clicks option link-relink at Banks section
   * @param {Event} event - The click event object.
   * @returns {void}
   */
  const linkOrRelinkAccountHandler = useCallback(
    (event) => {
      isArchived ? relinkHandler(event) : openUnlinkConfirmModal(event);

      handleClose(event);
    },
    [handleClose, isArchived, openUnlinkConfirmModal, relinkHandler]
  );
  const navigateToCounterpartyHistory = useCallback(() => {
    navigate(
      generatePath(`${bankingPath}/connected-accounts/:id`, {
        id
      })
    );
  }, [navigate, bankingPath, id]);

  const clickOnItemHandler = () => {
    if (usedAtDashboard) {
      navigateToCounterpartyHistory();
    }
  };

  const handleRelinkOrMoveMoney = useCallback(
    (event) => (isArchived ? relinkHandler(event) : handleClickMoveMoneyButton(event)),
    [isArchived, relinkHandler, handleClickMoveMoneyButton]
  );

  const getMenuItems = useCallback(() => {
    const menuItemWhenUsedAtDashboard = [
      // Move money option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="move-money-item"
        onClick={(event) => handleClickMoveMoneyButton(event)}
      >
        Move money
      </MenuItemConnectedAccount>,

      // View history option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="view-history-item"
        onClick={(event) => {
          event.stopPropagation();
          navigateToCounterpartyHistory();
        }}
      >
        View history
      </MenuItemConnectedAccount>,

      // Unlink/Relink option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="unlink-link-account"
        onClick={(event) => linkOrRelinkAccountHandler(event)}
        sx={{
          color: `${theme.palette.text.red}`
        }}
      >
        {isArchived ? relinkActionLabel : ' Unlink'}
      </MenuItemConnectedAccount>
    ];

    if (isArchived) {
      // Remove Move money option from menu list
      menuItemWhenUsedAtDashboard.shift();
    }

    const defaultMenuItems = [
      // Transaction history option
      <MenuItem
        aria-label="item-action"
        key="transaction-history"
        onClick={navigateToCounterpartyHistory}
      >
        Transaction history
      </MenuItem>,

      // Unlink option (conditionally rendered based on 'isArchived')
      !isArchived && (
        <MenuItem
          aria-label="item-action"
          key="unlink-link-account"
          onClick={(event) => openUnlinkConfirmModal(event)}
          sx={{
            color: `${theme.palette.text.red}`,
            fontWeight: 500
          }}
        >
          Unlink
        </MenuItem>
      )
    ];

    return usedAtDashboard ? menuItemWhenUsedAtDashboard : defaultMenuItems;
  }, [
    handleClickMoveMoneyButton,
    navigateToCounterpartyHistory,
    linkOrRelinkAccountHandler,
    theme.palette.text.red,
    isArchived,
    relinkActionLabel,
    openUnlinkConfirmModal,
    usedAtDashboard
  ]);

  const ButtonRelinkOrMoveMoney = useMemo(() => {
    // Hide this button on dashboard
    if (usedAtDashboard) {
      return null;
    }

    const buttonText = isArchived ? relinkActionLabel : 'Move money';

    return (
      <Button
        aria-label="relink or move money action"
        color="primary"
        disabled={disable}
        fullWidth
        onClick={(event) => handleRelinkOrMoveMoney(event)}
        size="lg"
        variant="outlined"
      >
        {buttonText}
      </Button>
    );
  }, [disable, isArchived, relinkActionLabel, usedAtDashboard, handleRelinkOrMoveMoney]);

  return (
    <LinkedBankAccountWrapper
      aria-label="link account item"
      isLatest={isLatest}
      usedAtDashboard={usedAtDashboard}
    >
      <AccountRow
        accountNumber={accountNumber}
        accountType={accountType}
        anchorEl={anchorEl}
        button={ButtonRelinkOrMoveMoney}
        clickOnItemHandler={clickOnItemHandler}
        disabled={disable}
        handleClose={handleClose}
        handleOpen={handleOpen}
        id={id}
        isOpenModal={isOpenModal}
        label={
          isArchived ? (
            <PaymentStatusLabel status={COUNTER_PARTY_STATUS_LABEL.NO_LONGER_LINKED} />
          ) : null
        }
        menuItems={getMenuItems()}
        name={bank}
        usedAt={usedAt}
      />
    </LinkedBankAccountWrapper>
  );
}
LinkedBankAccount.propTypes = {
  id: PropTypes.string.isRequired,
  bank: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  connectedType: PropTypes.string.isRequired,
  accountType: PropTypes.string.isRequired,
  fullAccountNumber: PropTypes.string,
  isACHSupported: PropTypes.bool,
  isWireSupported: PropTypes.bool,
  wire: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      street2: PropTypes.string
    }),
    routingNumber: PropTypes.string
  }),
  disable: PropTypes.bool,
  usedAt: PropTypes.string,
  isLatest: PropTypes.bool
};
